import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Result,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
} from "antd";
import { ArrowRightOutlined, SwapOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";

import { isArray } from "lodash";

import { queryApi } from "../store/actions/common";
import { triggerNotification } from "../store/directActions";

const { Text } = Typography;

interface OwnProps {}

type transferPageProps = OwnProps & PropsFromRedux;

const steps = [
  { title: "Find Pack" },
  { title: "Destination & Confirm" },
  { title: "Done" },
];

const TransferPackPage: React.FC<transferPageProps> = () => {
  const [findForm] = Form.useForm();
  const [emailForm] = Form.useForm();

  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);

  const [matches, setMatches] = useState<any[]>([]);
  const [selectedPack, setSelectedPack] = useState<any>(null);

  const [targetStore, setTargetStore] = useState<any>(null);
  const [targetEmail, setTargetEmail] = useState<string>("");

  const [result, setResult] = useState<any>(null);

  const lookupPack = (pack: any, game?: any): Promise<any> =>
    queryApi(
      `slot/transfer/pack/${pack}${game != null && game !== "" ? `?game=${game}` : ""}`,
      "GET"
    );

  const lookupTarget = (email: string): Promise<any> =>
    queryApi(`slot/transfer/target?email=${encodeURIComponent(email)}`, "GET");

  const doTransfer = (slotId: any, body: any): Promise<any> =>
    queryApi(`slot/transfer/${slotId}`, "PUT", body);

  useEffect(() => {
    findForm.resetFields();
    emailForm.resetFields();
  }, []);

  const resetAll = () => {
    findForm.resetFields();
    emailForm.resetFields();
    setMatches([]);
    setSelectedPack(null);
    setTargetStore(null);
    setTargetEmail("");
    setResult(null);
    setCurrent(0);
  };

  // ---- step 0: find the pack ----------------------------------------------
  const onFind = async (values: any) => {
    setLoading(true);
    try {
      const res = await lookupPack(values.pack_id, values.game);
      if (!isArray(res?.matches) || res.matches.length === 0) {
        triggerNotification({
          type: "error",
          title: "No pack found with this number",
          description: values.game ? `game ${values.game}` : "",
        });
        setMatches([]);
      } else {
        setMatches(res.matches);
      }
    } catch (e: any) {
      triggerNotification({
        type: "error",
        title: "Lookup failed",
        description: e?.message ?? "",
      });
    }
    setLoading(false);
  };

  const selectPack = (record: any) => {
    setSelectedPack(record);
    setTargetStore(null);
    setTargetEmail("");
    emailForm.resetFields();
    setCurrent(1);
  };

  // ---- step 1: resolve the destination ------------------------------------
  const onLookupTarget = async (values: any) => {
    setLoading(true);
    try {
      const res = await lookupTarget(values.email);
      if (res?.found) {
        setTargetStore(res.store);
        setTargetEmail(values.email);
      } else {
        setTargetStore(null);
        triggerNotification({
          type: "error",
          title: "Destination not found",
          description: res?.message ?? "",
        });
      }
    } catch (e: any) {
      triggerNotification({
        type: "error",
        title: "Destination lookup failed",
        description: e?.message ?? "",
      });
    }
    setLoading(false);
  };

  // state-match / visibility check (dest store state vs the game's provider)
  const providerId = selectedPack?.lottery?.provider_id;
  const stateMatch =
    targetStore != null && providerId != null
      ? targetStore.state_id === providerId
      : true;
  const sameStore =
    targetStore != null && selectedPack?.store?.store_id === targetStore.store_id;

  // ---- step 1: execute the transfer ---------------------------------------
  const onTransfer = async () => {
    if (!targetStore) return;
    setLoading(true);
    try {
      const res = await doTransfer(selectedPack.slot_id, {
        pack: selectedPack.pack,
        source_store_id: selectedPack.store?.store_id,
        target_email: targetEmail,
        game: selectedPack.game,
      });

      if (res?.status) {
        setResult(res);
        setCurrent(2);
      } else {
        triggerNotification({
          type: "error",
          title: "Transfer failed",
          description: res?.message ?? "",
        });
      }
    } catch (e: any) {
      triggerNotification({
        type: "error",
        title: "Transfer failed",
        description: e?.message ?? "",
      });
    }
    setLoading(false);
  };

  const matchColumns = [
    {
      title: "Game-Pack #",
      key: "game",
      render: (record: any) => (
        <div style={{ minWidth: "max-content" }}>
          {record.game}-{record.pack}
        </div>
      ),
    },
    {
      title: "Game",
      key: "name",
      render: (record: any) => record?.lottery?.name ?? "-",
    },
    {
      title: "Box #",
      dataIndex: "slot_number",
      key: "slot_number",
      render: (n: any) => <div>#{n}</div>,
    },
    {
      title: "Current Store",
      key: "store",
      render: (record: any) => (
        <div>
          <div>{record.store?.name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.store?.owner_email}
          </Text>
        </div>
      ),
    },
    {
      title: "State",
      key: "flags",
      render: (record: any) => (
        <Space size={4} wrap>
          {record.is_sold ? <Tag color="red">SOLD</Tag> : null}
          {record.is_closed ? <Tag color="default">CLOSED</Tag> : null}
          {!record.is_sold && !record.is_closed ? (
            <Tag color="green">TRANSFERABLE</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (record: any) => {
        const blocked = !!record.is_sold || !!record.is_closed;
        return (
          <Button
            type="link"
            disabled={blocked}
            title={blocked ? "Sold/closed packs cannot be transferred" : ""}
            icon={<ArrowRightOutlined />}
            onClick={() => selectPack(record)}
          />
        );
      },
    },
  ];

  return (
    <section id="transfer-pack" className="page-section h-page p-6 flex flex-col">
      <div className="mb-4">
        <h2 className="text-2xl font-bold app-text">
          Transfer Pack
        </h2>
        <p className="app-muted mt-1">
          Move a pack to a different store by the target owner's email. Changes
          the pack's store only, with an <code>ADMIN_TRANSFER_PACK</code> audit
          record.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="app-surface rounded-xl shadow-sm border border-solid app-border p-6">
          <Steps current={current} type="navigation" items={steps} />

          {/* -------------- STEP 0: FIND PACK -------------- */}
          {current === 0 && (
            <div>
              <Form
                form={findForm}
                layout="inline"
                onFinish={onFind}
                style={{ marginTop: 16, rowGap: 12 }}
              >
                <Form.Item
                  name="pack_id"
                  label="Pack Number"
                  rules={[
                    { required: true, message: "Please enter pack number" },
                  ]}
                >
                  <InputNumber
                    style={{ width: 180 }}
                    placeholder="Enter pack number"
                  />
                </Form.Item>
                <Form.Item name="game" label="Game # (optional)">
                  <InputNumber
                    style={{ width: 180 }}
                    placeholder="Disambiguate by game"
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    onClick={findForm.submit}
                    type="primary"
                    loading={loading}
                  >
                    Search
                  </Button>
                </Form.Item>
              </Form>

              {matches.length > 1 && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="info"
                  showIcon
                  message="Multiple packs match. Add a game number or pick the exact row below."
                />
              )}

              <div style={{ overflowY: "auto", marginTop: 16 }}>
                <Table
                  rowKey="slot_id"
                  columns={matchColumns}
                  dataSource={matches}
                  size="small"
                  tableLayout="auto"
                  pagination={false}
                  sticky
                />
              </div>
            </div>
          )}

          {/* -------------- STEP 1: DESTINATION & CONFIRM -------------- */}
          {current === 1 && selectedPack && (
            <div style={{ marginTop: 16 }}>
              <Form form={emailForm} layout="inline" onFinish={onLookupTarget}>
                <Form.Item
                  name="email"
                  label="Target owner email"
                  rules={[
                    { required: true, message: "Enter the destination email" },
                    { type: "email", message: "Enter a valid email" },
                  ]}
                >
                  <Input
                    style={{ width: 280 }}
                    placeholder="owner@example.com"
                    allowClear
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    onClick={emailForm.submit}
                    loading={loading}
                    icon={<SwapOutlined />}
                  >
                    Look up store
                  </Button>
                </Form.Item>
              </Form>

              <Card
                size="small"
                title="Transfer summary"
                style={{ marginTop: 16 }}
              >
                <Descriptions size="small" column={2} bordered>
                  <Descriptions.Item label="Pack">
                    {selectedPack.game}-{selectedPack.pack} (
                    {selectedPack.lottery?.name})
                  </Descriptions.Item>
                  <Descriptions.Item label="Slot / Box">
                    #{selectedPack.slot_id} / box #{selectedPack.slot_number}
                  </Descriptions.Item>
                  <Descriptions.Item label="FROM store">
                    {selectedPack.store?.name}
                    <br />
                    <Text type="secondary">
                      {selectedPack.store?.owner_email} · store #
                      {selectedPack.store?.store_id} · state{" "}
                      {selectedPack.store?.state_id}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="TO store">
                    {targetStore ? (
                      <span>
                        {targetStore.name}
                        <br />
                        <Text type="secondary">
                          {targetStore.owner_email} · store #
                          {targetStore.store_id} · state {targetStore.state_id}
                        </Text>
                      </span>
                    ) : (
                      <Text type="secondary">Look up an email first</Text>
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {targetStore && sameStore && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="error"
                  showIcon
                  message="Source and destination are the same store — nothing to transfer."
                />
              )}

              {targetStore && !sameStore && !stateMatch && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="warning"
                  showIcon
                  message="State mismatch — pack will move but won't display at the destination"
                  description={`This game's provider is state ${providerId}, but the destination store is state ${targetStore.state_id}. The pack will be reassigned but stays invisible on that store's screen until it matches.`}
                />
              )}

              <div style={{ marginTop: 16 }}>
                <Popconfirm
                  title="Transfer this pack?"
                  description={`Move pack ${selectedPack.pack} to ${targetStore?.name ?? ""}. This changes the pack's store and logs ADMIN_TRANSFER_PACK.`}
                  okText="Transfer"
                  disabled={!targetStore || sameStore}
                  onConfirm={onTransfer}
                >
                  <Button
                    type="primary"
                    loading={loading}
                    disabled={!targetStore || sameStore}
                    icon={<SwapOutlined />}
                  >
                    Transfer Pack
                  </Button>
                </Popconfirm>
                <Button
                  style={{ marginLeft: 8 }}
                  onClick={() => {
                    setSelectedPack(null);
                    setCurrent(0);
                  }}
                >
                  Back
                </Button>
              </div>
            </div>
          )}

          {/* -------------- STEP 2: DONE -------------- */}
          {current === 2 && result && (
            <div style={{ marginTop: 16 }}>
              <Result
                status={result.will_display ? "success" : "warning"}
                title={`Pack ${selectedPack?.pack} transferred`}
                subTitle={
                  result.will_display
                    ? "Logged as ADMIN_TRANSFER_PACK. The pack now belongs to the destination store."
                    : "Transferred and logged, but it won't display at the destination until the state matches."
                }
                extra={[
                  <Card
                    key="audit"
                    size="small"
                    title="Ledger audit (ADMIN_TRANSFER_PACK)"
                    style={{ textAlign: "left", marginBottom: 16 }}
                  >
                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontSize: 12,
                      }}
                    >
                      {JSON.stringify(result.ledger_extras, null, 2)}
                    </pre>
                  </Card>,
                  <Button type="primary" key="done" onClick={resetAll}>
                    Done
                  </Button>,
                ]}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  storeId: state.user?.profileData?.storeId,
});

const mapDispatchToProps = () => ({});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(TransferPackPage);
